<template>
  <div>
    <!-- Offline banner that appears at the top of the screen -->
    <div v-if="!network.isOnline" class="fixed top-0 left-0 right-0 bg-red-600 text-white py-2 px-4 text-center z-50">
      <i class="pi pi-wifi mr-2"></i>
      You are currently offline. Some features may not work properly.
    </div>
    
    <!-- Reconnected notification that appears briefly -->
    <div v-if="network.isOnline && wasOffline" class="fixed bottom-4 right-4 bg-green-600 text-white py-2 px-4 rounded-md z-50 animate-fade-out">
      <i class="pi pi-check-circle mr-2"></i>
      Connection restored!
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNetwork } from '@vueuse/core';
import { useToast } from 'primevue/usetoast';
import { watch, ref } from 'vue';
import { useEventBus } from '@/composables/useEventBus';

const toast = useToast();
const { emit } = useEventBus();
const network = useNetwork();
const wasOffline = ref(false);

// Show toast notifications when connection status changes
watch(() => network.isOnline.value, (newStatus, oldStatus) => {
  if (oldStatus === true && newStatus === false) {
    toast.add({
      severity: 'error',
      summary: 'Connection Lost',
      detail: 'You are currently offline. Some features may not work properly.',
      life: 5000
    });
    emit('network-status-changed', { isOnline: false });
  } else if (oldStatus === false && newStatus === true) {
    wasOffline.value = true;
    toast.add({
      severity: 'success',
      summary: 'Connection Restored',
      detail: 'You are back online!',
      life: 3000
    });
    emit('network-status-changed', { isOnline: true });
    
    // Reset wasOffline flag after 5 seconds
    setTimeout(() => {
      wasOffline.value = false;
    }, 5000);
  }
}, { immediate: false });
</script>

<style scoped>
.animate-fade-out {
  animation: fadeOut 5s forwards;
}

@keyframes fadeOut {
  0% { opacity: 1; }
  70% { opacity: 1; }
  100% { opacity: 0; visibility: hidden; }
}
</style>